import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import FileUploader from '../components/FileUploader';
import DocumentList from '../components/DocumentList';
import OllamaStatusBadge from '../components/OllamaStatusBadge';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { useToast } from '../components/common/ToastProvider';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const StatCard = ({ title, value, note }) => {
  return (
    <div className="admin-stat-card">
      <div>
        <div className="text-muted small fw-semibold mb-2">{title}</div>
        <h3 className="mb-1 fw-bold">{value}</h3>
        {note ? <div className="text-muted small">{note}</div> : null}
      </div>
    </div>
  );
};

function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [workspaceFilter, setWorkspaceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [msg, setMsg] = useState('');
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [planCatalog, setPlanCatalog] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', plan_code: 'free' });
  const [assigningUserId, setAssigningUserId] = useState(null);
  const [analytics, setAnalytics] = useState({ workspace: { daily: [], weekly: [], monthly: [] }, platform: { daily: [], weekly: [], monthly: [] } });
  const [analyticsPeriod, setAnalyticsPeriod] = useState('daily');
  const [auditLogs, setAuditLogs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (workspaceFilter) params.workspace_id = workspaceFilter;
      if (statusFilter) params.is_active = statusFilter;

      const [summaryRes, wsRes, widgetRes, usersRes, plansRes, analyticsRes, auditRes] = await Promise.all([
        api.get('/admin/settings/summary/'),
        api.get('/admin/global/workspaces/'),
        api.get('/admin/global/widgets/', { params }),
        api.get('/accounts/users/'),
        api.get('/admin/settings/plans/'),
        api.get('/admin/settings/analytics/'),
        api.get('/admin/settings/audit-logs/', { params: { page_size: 12 } }),
      ]);

      setSummary(summaryRes.data || null);
      setWorkspaces(Array.isArray(wsRes.data) ? wsRes.data : []);
      setWidgets(Array.isArray(widgetRes.data) ? widgetRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setPlanCatalog(Array.isArray(plansRes.data) ? plansRes.data : []);
      setAnalytics(analyticsRes.data || { workspace: { daily: [], weekly: [], monthly: [] }, platform: { daily: [], weekly: [], monthly: [] } });
      setAuditLogs(Array.isArray(auditRes.data?.results) ? auditRes.data.results : []);
      setMsg('');
    } catch (e) {
      const errorMessage = e?.response?.data?.error || 'Unable to load admin dashboard data';
      setMsg(errorMessage);
      showToast(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [workspaceFilter, statusFilter]);

  const setWidgetState = async (widgetId, action) => {
    try {
      setActionLoadingId(widgetId);
      await api.post('/admin/global/widgets/', { widget_id: widgetId, action });
      setMsg(`Widget ${action}d successfully.`);
      showToast(`Widget ${action}d successfully.`, 'success');
      await load();
    } catch (e) {
      const errorMessage = e?.response?.data?.error || 'Unable to update widget status';
      setMsg(errorMessage);
      showToast(errorMessage, 'danger');
    } finally {
      setActionLoadingId(null);
    }
  };

  const workspaceSummary = summary?.workspace_summary || {};
  const globalSummary = summary?.global_summary || {};
  const workspaceSeries = analytics?.workspace?.[analyticsPeriod] || [];
  const platformSeries = analytics?.platform?.[analyticsPeriod] || [];
  const makeChartData = (rows) => ({
    labels: rows.map((r) => r.period),
    datasets: [
      { label: 'Chats', data: rows.map((r) => r.chats), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,.18)', tension: 0.35 },
      { label: 'Payments', data: rows.map((r) => r.payments), borderColor: '#059669', backgroundColor: 'rgba(5,150,105,.18)', tension: 0.35 },
      { label: 'Handovers', data: rows.map((r) => r.handovers), borderColor: '#d97706', backgroundColor: 'rgba(217,119,6,.18)', tension: 0.35 },
    ],
  });

  const createUserWithPlan = async (e) => {
    e.preventDefault();
    try {
      await api.post('/accounts/users/', newUser);
      showToast('User created and plan assigned.', 'success');
      setNewUser({ username: '', email: '', password: '', plan_code: 'free' });
      await load();
    } catch (e2) {
      showToast(e2?.response?.data?.error || 'Failed to create user.', 'danger');
    }
  };

  const assignPlan = async (userId, planCode) => {
    if (!planCode) return;
    try {
      setAssigningUserId(userId);
      await api.post(`/accounts/users/${userId}/assign-plan/`, { plan_code: planCode });
      showToast('Plan assigned successfully.', 'success');
      await load();
    } catch (e2) {
      showToast(e2?.response?.data?.error || 'Failed to assign plan.', 'danger');
    } finally {
      setAssigningUserId(null);
    }
  };

  return (
    <AdminLayout
      title="Dashboard Overview"
      subtitle="Track workspaces, widget activity, training data, and operational health from one place."
    >
      {msg ? <Alert variant="info" className="admin-surface-card">{msg}</Alert> : null}

      <Row className="g-3 mb-4">
        <Col xl={3} md={6}>
          <StatCard
            title="Workspace Messages"
            value={workspaceSummary.chat_messages ?? 0}
            note="Recent workspace conversation volume"
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Workspace Widgets"
            value={workspaceSummary.widgets ?? 0}
            note="Active widget deployments"
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Training Sources"
            value={workspaceSummary.training_sources ?? 0}
            note="Knowledge sources uploaded"
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Payment Events"
            value={workspaceSummary.events ?? 0}
            note="Processed billing-related events"
          />
        </Col>

        <Col xl={3} md={6}>
          <StatCard
            title="Total Workspaces"
            value={globalSummary.workspaces ?? 0}
            note="All registered workspaces"
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Total Messages"
            value={globalSummary.chat_messages ?? 0}
            note="All chat messages across platform"
          />
        </Col>
        <Col xl={3} md={6}>
          <StatCard
            title="Payment Events"
            value={globalSummary.payment_events ?? 0}
            note="Global billing activity count"
          />
        </Col>
        <Col xl={3} md={6}>
          <div className="admin-stat-card">
            <div>
              <div className="text-muted small fw-semibold mb-2">System Check</div>
              <h6 className="mb-2 fw-bold">Ollama Status</h6>
              <div className="text-muted small">Model server availability</div>
            </div>
            <div className="mt-3">
              <OllamaStatusBadge />
            </div>
          </div>
        </Col>
      </Row>

      <Card className="admin-filter-card mb-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="admin-section-title mb-0">Analytics Trends</div>
          <div className="d-flex gap-2">
            {['daily', 'weekly', 'monthly'].map((p) => (
              <Button key={p} size="sm" variant={analyticsPeriod === p ? 'primary' : 'outline-primary'} onClick={() => setAnalyticsPeriod(p)}>
                {p[0].toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Card className="admin-surface-card h-100">
              <Card.Body>
                <div className="fw-semibold mb-2">Workspace Trends</div>
                <Line data={makeChartData(workspaceSeries)} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="admin-surface-card h-100">
              <Card.Body>
                <div className="fw-semibold mb-2">Platform Trends</div>
                <Line data={makeChartData(platformSeries)} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card className="admin-surface-card mb-4">
        <Card.Body>
          <div className="admin-section-title">Recent Audit Logs</div>
          <div className="table-responsive">
            <Table hover className="admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Category</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Workspace</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.created_at).toLocaleString()}</td>
                    <td>{row.category}</td>
                    <td>{row.action}</td>
                    <td>{row.actor_username || 'system'}</td>
                    <td>{row.workspace || '-'}</td>
                  </tr>
                ))}
                {!auditLogs.length ? <tr><td colSpan={5} className="text-center text-muted py-4">No audit events yet.</td></tr> : null}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="admin-filter-card mb-4">
        <div className="admin-section-title">Global Widget Management</div>

        <Row className="g-3 align-items-end">
          <Col lg={4} md={6}>
            <Form.Label className="fw-semibold">Workspace</Form.Label>
            <Form.Select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
            >
              <option value="">All workspaces</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.slug})
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col lg={3} md={6}>
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="true">Active only</option>
              <option value="false">Disabled only</option>
            </Form.Select>
          </Col>

          <Col lg={2} md={6}>
            <Button
              onClick={load}
              className="w-100 admin-action-btn"
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      <Card className="admin-surface-card mb-4">
        <Card.Body>
          <div className="admin-section-title">Widgets</div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="admin-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Workspace</th>
                    <th>Widget Name</th>
                    <th>Origin</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {widgets.length ? (
                    widgets.map((w) => (
                      <tr key={w.id}>
                        <td className="fw-semibold">{w.id}</td>
                        <td>{w.workspace}</td>
                        <td>{w.name}</td>
                        <td className="text-break">{w.allowed_origin}</td>
                        <td>
                          {w.is_active ? (
                            <span className="admin-pill admin-pill--success">Active</span>
                          ) : (
                            <span className="admin-pill admin-pill--danger">Disabled</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="admin-action-btn"
                              disabled={actionLoadingId === w.id}
                              onClick={() => setWidgetState(w.id, 'enable')}
                            >
                              Enable
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="admin-action-btn"
                              disabled={actionLoadingId === w.id}
                              onClick={() => setWidgetState(w.id, 'disable')}
                            >
                              Disable
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No widgets found for current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Row className="g-4">
        <Col xl={5}>
          <FileUploader />
        </Col>
        <Col xl={7}>
          <DocumentList />
        </Col>
      </Row>

      <Card className="admin-surface-card mt-4 mb-4">
        <Card.Body>
          <div className="admin-section-title">Create User & Assign Plan</div>
          <Form onSubmit={createUserWithPlan}>
            <Row className="g-3">
              <Col md={3}>
                <Form.Control
                  placeholder="Username"
                  required
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                />
              </Col>
              <Col md={3}>
                <Form.Control
                  type="email"
                  placeholder="Email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  required
                  minLength={8}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
              </Col>
              <Col md={2}>
                <Form.Select
                  value={newUser.plan_code}
                  onChange={(e) => setNewUser({ ...newUser, plan_code: e.target.value })}
                >
                  {planCatalog.map((plan) => (
                    <option key={plan.id} value={plan.code}>{plan.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button className="w-100 admin-action-btn" type="submit">
                  Create User
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="admin-surface-card mt-4">
        <Card.Body>
          <div className="admin-section-title">Users & Plan Assignment</div>
          <div className="table-responsive">
            <Table hover className="admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Current Plan</th>
                  <th>Assign Plan</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.email || '-'}</td>
                    <td>{u.subscription_plan || 'N/A'}</td>
                    <td style={{ minWidth: 220 }}>
                      <Form.Select
                        disabled={assigningUserId === u.id}
                        defaultValue=""
                        onChange={(e) => assignPlan(u.id, e.target.value)}
                      >
                        <option value="" disabled>Select plan</option>
                        {planCatalog.map((plan) => (
                          <option key={`${u.id}-${plan.id}`} value={plan.code}>{plan.name}</option>
                        ))}
                      </Form.Select>
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">No users found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </AdminLayout>
  );
}

export default AdminDashboard;
